'use client'

import { useState } from 'react'
import Link from 'next/link'
import { BsChevronDown, BsChevronRight, BsTree, BsBuilding, BsGeoAlt, BsTicketPerforated, BsCupStraw, BsStar } from 'react-icons/bs'
import { RateCtaLabel, StarRating } from '@/components/star-rating'
import { entityPath } from '@/lib/slug'

interface Entity {
  id: string
  slug: string
  name: string
  type: string
  ratingSummary?: {
    currentRating: number
    totalRatings: number
  } | null
  children?: Entity[]
}

interface ChildrenAccordionProps {
  children: Entity[]
}

function getEntityIcon(type: string) {
  switch (type) {
    case 'PARK': return <BsTree className="w-4 h-4" />
    case 'RESORT': return <BsBuilding className="w-4 h-4" />
    case 'LAND': return <BsGeoAlt className="w-4 h-4" />
    case 'AREA': return <BsGeoAlt className="w-4 h-4" />
    case 'ATTRACTION': return <BsTicketPerforated className="w-4 h-4" />
    case 'RESTAURANT': return <BsCupStraw className="w-4 h-4" />
    default: return <BsStar className="w-4 h-4" />
  }
}

function AccordionItem({ entity, level = 0 }: { entity: Entity; level?: number }) {
  const [isOpen, setIsOpen] = useState(false)
  const hasChildren = entity.children && entity.children.length > 0
  const isRateable = ['ATTRACTION', 'RESTAURANT', 'SHOW', 'ENTERTAINMENT', 'RESORT'].includes(entity.type)

  const paddingLeft = level * 16 + 12

  // Rateable item without children - just a link
  if (isRateable && !hasChildren) {
    return (
      <div className="border-b border-slate-100 last:border-0">
        <Link
          href={entityPath(entity)}
          className="flex items-center gap-3 py-2 px-3 hover:bg-[#1E4289]/5 transition-colors"
          style={{ paddingLeft }}
        >
          <span className="text-[#1E4289]">{getEntityIcon(entity.type)}</span>
          <span className="flex-1 text-sm text-slate-700 hover:text-[#1E4289]">{entity.name}</span>
          {entity.ratingSummary && entity.ratingSummary.totalRatings > 0 && (
            <StarRating rating={entity.ratingSummary.currentRating} size="sm" showNumber />
          )}
        </Link>
        <div className="px-3 pb-2" style={{ paddingLeft }}>
          <Link href={entityPath(entity)}>
            <RateCtaLabel />
          </Link>
        </div>
      </div>
    )
  }

  // Resort with children - both rateable and expandable
  if (entity.type === 'RESORT' && hasChildren) {
    return (
      <div className="border-b border-slate-200 last:border-0">
        <div
          className="flex items-center gap-3 py-3 px-3 hover:bg-[#1E4289]/5 transition-colors"
          style={{ paddingLeft }}
        >
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="text-slate-400 hover:text-slate-600"
          >
            {isOpen ? <BsChevronDown className="w-3 h-3" /> : <BsChevronRight className="w-3 h-3" />}
          </button>
          <span className="text-[#1E4289]">{getEntityIcon(entity.type)}</span>
          <Link
            href={entityPath(entity)}
            className="flex-1 font-medium text-slate-800 hover:text-[#1E4289]"
          >
            {entity.name}
          </Link>
          {entity.ratingSummary && entity.ratingSummary.totalRatings > 0 && (
            <StarRating rating={entity.ratingSummary.currentRating} size="sm" showNumber />
          )}
        </div>
        <div className="px-3 pb-2" style={{ paddingLeft }}>
          <Link href={entityPath(entity)}>
            <RateCtaLabel />
          </Link>
        </div>

        {isOpen && (
          <div className="bg-slate-50/50">
            {entity.children!.map((child) => (
              <AccordionItem key={child.id} entity={child} level={level + 1} />
            ))}
          </div>
        )}
      </div>
    )
  }

  // Non-rateable expandable item (like AREA, LAND)
  return (
    <div className="border-b border-slate-200 last:border-0">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center gap-3 py-3 px-3 hover:bg-[#1E4289]/5 transition-colors text-left"
        style={{ paddingLeft }}
      >
        <span className="text-slate-400">
          {isOpen ? <BsChevronDown className="w-3 h-3" /> : <BsChevronRight className="w-3 h-3" />}
        </span>
        <span className="text-[#1E4289]">{getEntityIcon(entity.type)}</span>
        <span className="flex-1 font-medium text-slate-800">{entity.name}</span>
      </button>
      
      {isOpen && hasChildren && (
        <div className="bg-slate-50/50">
          {entity.children!.map((child) => (
            <AccordionItem key={child.id} entity={child} level={level + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

const TYPE_ORDER = ['LAND', 'AREA', 'ATTRACTION', 'RESTAURANT', 'SHOW', 'ENTERTAINMENT', 'RESORT']

const TYPE_LABELS: Record<string, string> = {
  LAND: 'Lands',
  AREA: 'Areas',
  ATTRACTION: 'Attractions',
  RESTAURANT: 'Restaurants',
  SHOW: 'Shows',
  ENTERTAINMENT: 'Entertainment',
  RESORT: 'Resorts',
}

export function ChildrenAccordion({ children }: ChildrenAccordionProps) {
  if (!children || children.length === 0) {
    return null
  }

  // Group children by type
  const groupedByType = children.reduce((acc, child) => {
    const type = child.type
    if (!acc[type]) {
      acc[type] = []
    }
    acc[type].push(child)
    return acc
  }, {} as Record<string, Entity[]>)

  // Sort types by defined order
  const sortedTypes = Object.keys(groupedByType).sort((a, b) => {
    const indexA = TYPE_ORDER.indexOf(a)
    const indexB = TYPE_ORDER.indexOf(b)
    return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB)
  })

  return (
    <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-hidden">
      {sortedTypes.map((type) => (
        <div key={type}>
          <div className="bg-[#1E4289] text-white px-4 py-2 font-semibold flex items-center gap-2">
            {getEntityIcon(type)}
            {TYPE_LABELS[type] || type}
          </div>
          {groupedByType[type].map((child) => (
            <AccordionItem key={child.id} entity={child} />
          ))}
        </div>
      ))}
    </div>
  )
}

